import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getIndustryChildren,
  getIndustryPath,
  industriesQueryKeys,
  INDUSTRY_LEVEL_LABELS,
  searchIndustries,
  type IndustryOption,
} from '../../lib/industries';

/**
 * 현장 업종 분류(KSIC) picker — 한 단계씩 내려가는 select 사다리 + 검색.
 *
 * ## 정책(사용자 결정 2026-10-30 · `docs/work-streams/backlog/industry-classification-canonical.md`)
 * - **어느 단계에서 멈춰도 저장된다** — 서버도 단계를 묻지 않는다(`assertIndustrySelectable`).
 * - 화면은 **가장 세세한 분류까지 고르게 유도**한다: 고른 업종에 더 내려갈 자식이 있으면(`hasChildren`)
 *   안내 문구를 보이고, 검색 결과는 서버가 세세분류를 먼저 준다. 「세세분류인가」를 `level === 5`가 아니라
 *   `hasChildren`으로 보는 이유: 말단이 5단계보다 얕은 행이 생겨도 «더 고를 것이 없는데 안내가 남는» 일이 없게.
 *
 * 한 노드의 자식이 최대 25개라(서버 `IndustriesService` docblock) 트리 통째(2,038행)가 아니라 단계별로 읽는다.
 * 현장 업종의 **유일한 입력**이다(2026-10-30 — 8개 하드코딩 목록·「세부 업종」 자유 텍스트를 대체). 법정 서류 적용
 * 판정·AI 업종 템플릿·건설 단계 노출이 이 칸에서 파생한다.
 */
export interface IndustryPickerProps {
  /** `Industry.id` — 빈 문자열이면 미지정 */
  value: string;
  /** 고른 업종(지우면 `''`·null) — `option.code`로 부르는 쪽이 건설 판정 등을 파생한다 */
  onChange: (industryId: string, option: IndustryOption | null) => void;
  disabled?: boolean;
  selectClassName: string;
  labelClassName: string;
  hintClassName?: string;
}

/**
 * 확정 문구(사용자 2026-10-30). 바꾸면 짝 spec의 문구 단언도 함께.
 */
export const INDUSTRY_DEEPER_HINT = '세세분류까지 선택하면 해당되는 법정 서류를 더 정확하게 안내해 드립니다.';

const STALE_MS = 30 * 60 * 1000;

function optionLabel(o: IndustryOption): string {
  return `${o.code} · ${o.name}`;
}

function LevelSelect({
  parentId,
  depth,
  selectedId,
  disabled,
  className,
  onPick,
}: {
  parentId: string | null;
  depth: number;
  selectedId: string;
  disabled?: boolean;
  className: string;
  onPick: (option: IndustryOption | null) => void;
}) {
  const query = useQuery({
    queryKey: industriesQueryKeys.children(parentId),
    queryFn: () => getIndustryChildren(parentId),
    staleTime: STALE_MS,
  });
  const options = query.data ?? [];
  const levelLabel = INDUSTRY_LEVEL_LABELS[depth + 1] ?? `${depth + 1}단계`;
  return (
    <select
      aria-label={`업종 분류 ${levelLabel}`}
      value={selectedId}
      disabled={disabled || query.isLoading}
      className={className}
      onChange={(e) => onPick(options.find((o) => o.id === e.target.value) ?? null)}
    >
      <option value="">{depth === 0 ? '선택하세요' : `${levelLabel} 선택 (여기서 멈춰도 저장됩니다)`}</option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {optionLabel(o)}
        </option>
      ))}
    </select>
  );
}

export function IndustryPicker({
  value,
  onChange,
  disabled,
  selectClassName,
  labelClassName,
  hintClassName = 'mt-1 text-xs text-ink-500',
}: IndustryPickerProps) {
  // 화면이 보여 주는 사다리 — 대분류부터 고른 업종까지. 값이 바뀌면(편집 진입·검색으로 고름) 서버 경로로 다시 세운다
  const [path, setPath] = useState<IndustryOption[]>([]);
  const tailId = path.length > 0 ? path[path.length - 1].id : '';
  const needsPath = value !== '' && tailId !== value;

  const pathQuery = useQuery({
    queryKey: industriesQueryKeys.path(value),
    queryFn: () => getIndustryPath(value),
    enabled: needsPath,
    staleTime: STALE_MS,
  });
  useEffect(() => {
    if (value === '') {
      if (path.length > 0) setPath([]);
      return;
    }
    if (needsPath && pathQuery.data) setPath(pathQuery.data);
  }, [value, needsPath, pathQuery.data, path.length]);

  const [searchText, setSearchText] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(searchText.trim()), 250);
    return () => clearTimeout(t);
  }, [searchText]);
  const searchQuery = useQuery({
    queryKey: industriesQueryKeys.search(debounced),
    queryFn: () => searchIndustries(debounced),
    enabled: debounced.length > 0,
    staleTime: STALE_MS,
  });

  const pickAt = (depth: number, option: IndustryOption | null) => {
    const kept = path.slice(0, depth);
    const next = option ? [...kept, option] : kept;
    setPath(next);
    const picked = next.length > 0 ? next[next.length - 1] : null;
    onChange(picked?.id ?? '', picked);
  };

  const pickFromSearch = (option: IndustryOption) => {
    setSearchText('');
    setDebounced('');
    // 경로는 서버에서 다시 받는다(검색 결과는 조상을 싣지 않는다) — 위 effect가 세운다
    setPath([]);
    onChange(option.id, option);
  };

  const tail = path.length > 0 ? path[path.length - 1] : null;
  // 경로를 못 받으면(그 업종이 지워졌다 등) 사다리를 대분류부터 다시 열어 고쳐 고를 수 있게 한다 — 막다른 길 금지
  const pathFailed = needsPath && pathQuery.isError;
  const waitingPath = needsPath && !pathFailed;
  // 보여 줄 select 수 — 고른 단계들 + (더 내려갈 수 있으면) 다음 단계 하나. 경로를 받는 중이면 사다리를 비운다
  const showNext = !waitingPath && (tail === null || tail.hasChildren);
  const depths = waitingPath ? [] : [...path.map((_, i) => i), ...(showNext ? [path.length] : [])];

  const results = searchQuery.data ?? [];

  return (
    <div className="space-y-2">
      <div className="grid gap-2 sm:grid-cols-2">
        {depths.map((depth) => (
          <LevelSelect
            key={`${depth}:${depth === 0 ? 'root' : path[depth - 1].id}`}
            parentId={depth === 0 ? null : path[depth - 1].id}
            depth={depth}
            selectedId={path[depth]?.id ?? ''}
            disabled={disabled}
            className={selectClassName}
            onPick={(o) => pickAt(depth, o)}
          />
        ))}
        {waitingPath && <p className={hintClassName}>업종 분류를 불러오는 중…</p>}
        {pathFailed && <p className={hintClassName}>저장된 업종 분류를 불러오지 못했습니다. 다시 골라 주세요.</p>}
      </div>

      <div>
        <label htmlFor="site-industry-search" className={labelClassName}>
          업종 분류 검색
        </label>
        <input
          id="site-industry-search"
          type="search"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          // 검색 칸의 Enter는 «검색»이지 «저장»이 아니다 — 이 피커는 현장 폼(`SiteFormModal`의 `<form>`) 안에
          // 그려지므로 막지 않으면 Enter가 바깥 폼을 암묵 제출해 수정 저장·생성 다음 단계로 넘어갔다(리뷰 1103 F18)
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.preventDefault();
          }}
          disabled={disabled}
          className={selectClassName}
          placeholder="예: 금속 가공, C25"
        />
        {debounced.length > 0 && (
          <ul className="mt-1 max-h-56 overflow-y-auto rounded-control border border-ink-200 bg-white text-sm" role="listbox" aria-label="업종 분류 검색 결과">
            {results.length === 0 && !searchQuery.isLoading && (
              <li className="px-3 py-2 text-ink-500">검색 결과가 없습니다.</li>
            )}
            {results.map((o) => (
              <li key={o.id} role="option" aria-selected={o.id === value}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-ink-50"
                  onClick={() => pickFromSearch(o)}
                >
                  <span className="min-w-0 truncate">{optionLabel(o)}</span>
                  <span className="shrink-0 text-xs text-ink-500">{INDUSTRY_LEVEL_LABELS[o.level] ?? `${o.level}단계`}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {tail && tail.hasChildren && <p className={hintClassName}>{INDUSTRY_DEEPER_HINT}</p>}
    </div>
  );
}
